import React from 'react';
import { Box, Checkbox, FormControlLabel, FormHelperText } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { DatePicker } from '../../Paylinks/components';
import {
  BaseFilterSection as Section,
  filterSelectAllRowSx,
} from '../../Paylinks/components/BaseFiltersPanel';
import {
  portalUserAuditLogFilterOptions,
  PortalUserAuditLogFilterOption,
} from '../../../store/modules/merchant/types';
import { PortalUserAuditLogFiltersFormValues } from '../../../hooks/usePortalUserAuditLogFiltersForm';
import { getPortalUserAuditLogFilterOptionLabel } from '../portalUserAuditLogActionLabels';

interface Props {
  hookFormProps: {
    watch: UseFormWatch<PortalUserAuditLogFiltersFormValues>;
    setValue: UseFormSetValue<PortalUserAuditLogFiltersFormValues>;
    control: Control<PortalUserAuditLogFiltersFormValues>;
    errors: FieldErrors<PortalUserAuditLogFiltersFormValues>;
  };
}

export const PortalUserAuditLogFilters = ({ hookFormProps }: Props) => {
  const { watch, setValue, control, errors } = hookFormProps;
  const selectedActions = watch('actions');
  const createdAfter = watch('createdAfter');
  const createdBefore = watch('createdBefore');

  return (
    <>
      <Section
        title="Actions"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '& > *': {
            height: '24px',
            width: 'fit-content',
          },
          '& span': {
            fontSize: 14,
          },
        }}
      >
        <FormControlLabel
          sx={filterSelectAllRowSx}
          control={
            <Checkbox
              indeterminate={
                selectedActions.length > 0 &&
                selectedActions.length < portalUserAuditLogFilterOptions.length
              }
              checked={selectedActions.length === portalUserAuditLogFilterOptions.length}
              onChange={(e) =>
                e.target.checked
                  ? setValue('actions', [
                      ...portalUserAuditLogFilterOptions,
                    ] as PortalUserAuditLogFilterOption[])
                  : setValue('actions', [])
              }
            />
          }
          label="Select All"
        />
        {portalUserAuditLogFilterOptions.map((option) => (
          <FormControlLabel
            key={option}
            control={
              <Checkbox
                checked={selectedActions.includes(option)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('actions', [...selectedActions, option]);
                  } else {
                    setValue(
                      'actions',
                      selectedActions.filter((selected) => selected !== option),
                    );
                  }
                }}
              />
            }
            label={getPortalUserAuditLogFilterOptionLabel(option)}
          />
        ))}
        {errors.actions && (
          <FormHelperText error sx={{ fontSize: 10, mt: 0.5 }}>
            {errors.actions.message}
          </FormHelperText>
        )}
      </Section>
      <Section title="Date Range">
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['createdAfter', 'createdBefore'] as const).map((name) => (
            <Controller
              key={name}
              name={name}
              control={control}
              render={({ field }) => (
                <DatePicker
                  key={name}
                  label={name === 'createdAfter' ? 'Start date' : 'End date'}
                  errorMessage={errors[name]?.message}
                  disablePast={false}
                  dateOnly
                  shouldDisableDate={(date) => {
                    if (name === 'createdAfter' && createdBefore) {
                      return date.isAfter(createdBefore);
                    }

                    if (name === 'createdBefore' && createdAfter) {
                      return date.isBefore(createdAfter);
                    }

                    return false;
                  }}
                  closeOnSelect
                  {...field}
                />
              )}
            />
          ))}
        </Box>
      </Section>
    </>
  );
};
